import React, { useState } from 'react';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent
} from '@mui/lab';
import { 
  Typography, 
  Paper, 
  Box, 
  Chip, 
  Divider, 
  useTheme, 
  alpha,
  Fade,
  Zoom,
  Tooltip,
  useMediaQuery,
  IconButton,
  Collapse,
  Avatar
} from '@mui/material';
import { 
  PlayArrow, 
  Pause, 
  Stop, 
  Work, 
  AccessTime as TimeIcon,
  MoreTime as DurationIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  Code as CodeIcon,
  Description as DescriptionIcon,
  CalendarToday as CalendarIcon,
  DesignServices as DesignIcon,
  Settings as SettingsIcon,
  BugReport as BugIcon,
  Article as ArticleIcon
} from '@mui/icons-material';
import { format, parseISO, differenceInDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const formatDuration = (seconds) => {
  if (!seconds) return '0min';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  
  if (hours > 0) {
    return `${hours}h${minutes}min`;
  }
  return `${minutes}min`;
};

// Função para obter um ícone baseado na descrição do trabalho
const getWorkIcon = (description) => {
  const lowerDesc = description.toLowerCase();
  
  if (lowerDesc.includes('desenvolv') || lowerDesc.includes('program') || lowerDesc.includes('coding')) {
    return <CodeIcon />;
  } else if (lowerDesc.includes('design') || lowerDesc.includes('layout')) {
    return <DesignIcon />;
  } else if (lowerDesc.includes('bug') || lowerDesc.includes('fix') || lowerDesc.includes('corrigir')) {
    return <BugIcon />;
  } else if (lowerDesc.includes('test') || lowerDesc.includes('testar')) {
    return <SettingsIcon />;
  } else if (lowerDesc.includes('documenta') || lowerDesc.includes('relat')) {
    return <ArticleIcon />;
  } else if (lowerDesc.includes('reuni') || lowerDesc.includes('meet')) {
    return <PersonIcon />;
  } else {
    return <DescriptionIcon />;
  }
};

const WorkTimelineItem = ({ record, index, totalRecords }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [expanded, setExpanded] = useState(false);
  
  const handleExpandClick = () => {
    setExpanded(!expanded);
  };
  
  const getStatusIcon = (status) => {
    switch (status) {
      case 'in_progress':
        return <PlayArrow />;
      case 'paused':
        return <Pause />;
      case 'completed':
        return <Stop />;
      default:
        return <Work />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'in_progress':
        return 'primary';
      case 'paused':
        return 'warning';
      case 'completed':
        return 'success';
      default:
        return 'grey';
    }
  };

  const getTimelineBackground = (status) => {
    switch (status) {
      case 'in_progress':
        return alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.15 : 0.05);
      case 'paused':
        return alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.15 : 0.05);
      case 'completed':
        return alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.15 : 0.05);
      default:
        return alpha(theme.palette.grey[500], theme.palette.mode === 'dark' ? 0.15 : 0.05);
    }
  };
  
  // Determinar a duração do trabalho para adicionar contexto visual
  const getDurationCategory = (seconds) => {
    if (!seconds) return 'short';
    const hours = seconds / 3600;
    if (hours < 1) return 'short';
    if (hours < 4) return 'medium';
    return 'long';
  };
  
  const durationCategory = getDurationCategory(record.total_time_seconds);
  const hasPauses = record.pauses && record.pauses.length > 0;
  
  const statusText = {
    in_progress: 'Em progresso',
    paused: 'Pausado',
    completed: 'Concluído'
  };
  
  const workIcon = getWorkIcon(record.description);
  
  return (
    <TimelineItem>
      <TimelineOppositeContent sx={{ display: isMobile ? 'none' : 'block' }}>
        <Box sx={{ position: 'relative' }}>
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ 
              fontWeight: 500,
              display: 'flex', 
              alignItems: 'center',
              gap: 0.5,
              mb: 0.5
            }}
          >
            <CalendarIcon fontSize="small" color="action" />
            {format(parseISO(record.start_time), "dd/MM/yyyy", { locale: ptBR })}
          </Typography>
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              gap: 0.5 
            }}
          >
            <TimeIcon fontSize="small" color="action" />
            {format(parseISO(record.start_time), "HH:mm", { locale: ptBR })}
            {record.end_time && (
              ` - ${format(parseISO(record.end_time), "HH:mm", { locale: ptBR })}`
            )}
          </Typography>
          
          {record.total_time_seconds && (
            <Chip 
              icon={<DurationIcon fontSize="small" />}
              label={formatDuration(record.total_time_seconds)}
              size="small"
              variant="outlined"
              color={
                durationCategory === 'short' ? 'success' :
                durationCategory === 'medium' ? 'primary' : 'warning'
              }
              sx={{ 
                mt: 1,
                fontWeight: 500,
                fontSize: '0.7rem'
              }}
            />
          )}
        </Box>
      </TimelineOppositeContent>
      
      <TimelineSeparator>
        <Zoom in timeout={300 + (index * 150)}>
          <TimelineDot 
            sx={{ 
              bgcolor: theme.palette[getStatusColor(record.status)].main,
              boxShadow: theme.shadows[3],
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'scale(1.2)',
                boxShadow: theme.shadows[6]
              },
              p: 0
            }}
          >
            <Avatar
              sx={{
                width: 40,
                height: 40,
                bgcolor: 'transparent'
              }}
            >
              {getStatusIcon(record.status)}
            </Avatar>
          </TimelineDot>
        </Zoom>
        {index < totalRecords - 1 && (
          <TimelineConnector 
            sx={{ 
              minHeight: '60px',
              background: `linear-gradient(to bottom, 
                ${theme.palette[getStatusColor(record.status)].main}, 
                ${theme.palette[getStatusColor(record.status)].light})`,
              width: '3px'
            }}
          />
        )}
      </TimelineSeparator>
      
      <TimelineContent sx={{ px: { xs: 2, sm: 3 }, py: '12px' }}>
        <Fade in timeout={500 + (index * 200)}>
          <Paper 
            elevation={2} 
            sx={{ 
              p: { xs: 2, sm: 3 }, 
              mb: 2,
              borderRadius: '12px',
              boxShadow: `0 4px 20px ${alpha(theme.palette.grey[800], 0.1)}`,
              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              backgroundColor: getTimelineBackground(record.status),
              borderLeft: `4px solid ${theme.palette[getStatusColor(record.status)].main}`,
              overflow: 'hidden',
              position: 'relative',
              '&:hover': {
                boxShadow: `0 8px 30px ${alpha(theme.palette.grey[800], 0.2)}`,
                transform: 'translateY(-3px)'
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                background: `radial-gradient(circle at top left, ${alpha(theme.palette[getStatusColor(record.status)].main, 0.1)}, transparent 80%)`,
                zIndex: 0
              }
            }}
          >
            <Box 
              sx={{ 
                position: 'relative', 
                zIndex: 1
              }}
            >
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between', 
                alignItems: { xs: 'flex-start', sm: 'center' },
                mb: 1.5,
                gap: 1
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1.5
                }}>
                  <Avatar
                    sx={{
                      bgcolor: alpha(theme.palette[getStatusColor(record.status)].main, 0.2),
                      color: theme.palette[getStatusColor(record.status)].main,
                      display: { xs: 'none', sm: 'flex' }
                    }}
                  >
                    {workIcon}
                  </Avatar>
                  
                  <Typography 
                    variant="subtitle1" 
                    component="h3"
                    sx={{ 
                      fontWeight: 600,
                      lineHeight: 1.3
                    }}
                  >
                    {record.description}
                  </Typography>
                </Box>
                
                <Box sx={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: 1, 
                  mt: { xs: 0.5, sm: 0 },
                  ml: { xs: 0, sm: 'auto' }
                }}>
                  <Chip 
                    size="small"
                    label={statusText[record.status] || 'Desconhecido'}
                    color={getStatusColor(record.status)}
                    icon={getStatusIcon(record.status)}
                    sx={{ 
                      fontWeight: 500,
                      px: 0.5
                    }}
                  />
                  
                  {isMobile && record.total_time_seconds && (
                    <Tooltip title="Duração total">
                      <Chip 
                        size="small"
                        label={formatDuration(record.total_time_seconds)}
                        variant="outlined"
                        color={
                          durationCategory === 'short' ? 'success' :
                          durationCategory === 'medium' ? 'primary' : 'warning'
                        }
                        icon={<DurationIcon fontSize="small" />}
                        sx={{ fontWeight: 500 }}
                      />
                    </Tooltip>
                  )}
                  
                  {isMobile && (
                    <Tooltip title={`Início: ${format(parseISO(record.start_time), "dd/MM/yyyy HH:mm", { locale: ptBR })}`}>
                      <Chip 
                        size="small"
                        label={format(parseISO(record.start_time), "dd/MM HH:mm", { locale: ptBR })}
                        variant="outlined"
                        color="default"
                        icon={<TimeIcon fontSize="small" />}
                      />
                    </Tooltip>
                  )}
                </Box>
              </Box>
              
              {!isMobile && record.total_time_seconds && (
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 0.5, 
                    mb: 1,
                    mt: 0.5
                  }}
                >
                  <DurationIcon fontSize="small" color="action" />
                  Tempo total: <b>{formatDuration(record.total_time_seconds)}</b>
                </Typography>
              )}
              
              {hasPauses && (
                <Box 
                  sx={{
                    mt: 2,
                    position: 'relative'
                  }}
                >
                  <Box 
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                  >
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 0.5,
                        fontWeight: 500,
                        cursor: 'pointer'
                      }}
                      onClick={handleExpandClick}
                    >
                      <InfoIcon fontSize="small" color="warning" />
                      {record.pauses.length} {record.pauses.length === 1 ? 'pausa registrada' : 'pausas registradas'}
                    </Typography>
                    
                    <IconButton
                      onClick={handleExpandClick}
                      aria-expanded={expanded}
                      aria-label="mostrar pausas"
                      size="small"
                      sx={{
                        transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: theme.transitions.create('transform', {
                          duration: theme.transitions.duration.shortest,
                        }),
                      }}
                    >
                      <ExpandMoreIcon />
                    </IconButton>
                  </Box>
                  
                  <Collapse in={expanded} timeout="auto" unmountOnExit>
                    <Box 
                      sx={{
                        p: 2,
                        mt: 1,
                        bgcolor: alpha(theme.palette.background.paper, 0.6),
                        borderRadius: '8px',
                        border: `1px solid ${theme.palette.divider}`
                      }}
                    >
                      <Divider sx={{ mb: 2 }}>
                        <Chip label="Detalhes das pausas" size="small" />
                      </Divider>
                      
                      {record.pauses.map((pause, idx) => (
                        <Box 
                          key={pause.pause_id} 
                          sx={{ 
                            mb: idx < record.pauses.length - 1 ? 2 : 0,
                            position: 'relative',
                            pb: 2
                          }}
                        >
                          <Box 
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'flex-start',
                              gap: 1,
                              pl: 2,
                              borderLeft: `2px solid ${theme.palette.warning.main}`,
                              position: 'relative',
                              '&::before': {
                                content: '""',
                                position: 'absolute',
                                left: -4,
                                top: 0,
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                backgroundColor: theme.palette.warning.main
                              }
                            }}
                          >
                            <Box>
                              <Typography 
                                variant="body2"
                                sx={{ 
                                  fontWeight: 500,
                                  lineHeight: 1.5
                                }}
                              >
                                {pause.reason}
                              </Typography>
                              
                              {pause.total_pause_time_seconds && (
                                <Box 
                                  sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    mt: 0.5,
                                    gap: 1
                                  }}
                                >
                                  <Chip 
                                    icon={<DurationIcon fontSize="small" />}
                                    label={`${formatDuration(pause.total_pause_time_seconds)}`}
                                    size="small"
                                    variant="outlined"
                                    color="warning"
                                    sx={{ fontWeight: 500, fontSize: '0.7rem' }}
                                  />
                                  
                                  {pause.start_time && pause.end_time && (
                                    <Typography 
                                      variant="caption" 
                                      color="text.secondary"
                                      sx={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: 0.5
                                      }}
                                    >
                                      <TimeIcon fontSize="inherit" />
                                      {format(parseISO(pause.start_time), "HH:mm", { locale: ptBR })} - 
                                      {format(parseISO(pause.end_time), "HH:mm", { locale: ptBR })}
                                    </Typography>
                                  )}
                                </Box>
                              )}
                            </Box>
                          </Box>
                          
                          {idx < record.pauses.length - 1 && (
                            <Divider sx={{ mt: 2 }} />
                          )}
                        </Box>
                      ))}
                    </Box>
                  </Collapse>
                </Box>
              )}
            </Box>
          </Paper>
        </Fade>
      </TimelineContent>
    </TimelineItem>
  );
};

const WorkTimeline = ({ records }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  if (records.length === 0) {
    return (
      <Paper 
        elevation={0} 
        sx={{ 
          p: 4, 
          textAlign: 'center',
          borderRadius: '8px',
          backgroundColor: theme => theme.palette.mode === 'dark' 
            ? 'rgba(255, 255, 255, 0.05)' 
            : 'rgba(0, 0, 0, 0.03)',
        }}
      >
        <Typography color="text.secondary">
          Nenhum registro de trabalho encontrado.
        </Typography>
      </Paper>
    );
  }

  return (
    <Timeline 
      position={isMobile ? "right" : "alternate"}
      sx={{ 
        p: 0,
        mt: 1,
        [`& .MuiTimelineItem-root:before`]: {
          // Remove espaço para conteúdo oposto em telas pequenas
          flex: isMobile ? 0 : 1,
          padding: isMobile ? 0 : 'inherit'
        }
      }}
    >
      {records.map((record, index) => (
        <WorkTimelineItem 
          key={record.record_id} 
          record={record} 
          index={index}
          totalRecords={records.length}
        />
      ))}
    </Timeline>
  );
};

export default WorkTimeline; 